import { c as _c } from "react/compiler-runtime";
import { type ReactNode, type RefObject, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useIsTouchDevice, useResizeObserver } from '../../../../hooks';
import calculateColumnWidths from '../calculateColumnWidths';
import type { ColumnWidth } from '../calculateColumnWidths';
import { useColumns, useExpandable, useIsPivot, usePivotEntities, useTableId } from './selectors';
const ResizeContext = createContext<{
  columnWidths: Array<ColumnWidth>;
  resizing: boolean;
  onResize: (delta: number, colIndex: number) => void;
  setResizing: (resizing: boolean) => void;
  touchEnabled: boolean | null;
}>({
  onResize: () => {},
  columnWidths: [],
  resizing: false,
  setResizing: () => {},
  touchEnabled: null
});
export function useResizeContext() {
  return useContext(ResizeContext);
}
export function useColumnWidths(columnIndex) {
  const $ = _c(6);
  const columnWidths = useResizeContext().columnWidths;
  const columns = useColumns();
  const columnId = columns[columnIndex];
  let t0;
  const res = columnWidths.findIndex(c => c.field === columnId);
  if (res === -1) {
    throw new Error(`Column width not found for index ${columnId}`);
  }
  t0 = res;
  const index = t0;
  let t1;
  const res_ = columnWidths[index];
  if (!res_) {
    throw new Error(`Column width not found for index ${columnId}`);
  }
  const {
    resizable,
    lastResizable,
    maxWidth,
    minWidth,
    width
  } = res_;
  let t2;
  if ($[0] !== lastResizable || $[1] !== maxWidth || $[2] !== minWidth || $[3] !== resizable || $[4] !== width) {
    t2 = {
      resizable,
      lastResizable,
      maxWidth,
      minWidth,
      width
    };
    $[0] = lastResizable;
    $[1] = maxWidth;
    $[2] = minWidth;
    $[3] = resizable;
    $[4] = width;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  t1 = t2;
  const res_0 = t1;
  return res_0;
}
function useLocalStorage(key, initialValue) {
  const $ = _c(9);
  let t0;
  if ($[0] !== initialValue || $[1] !== key) {
    t0 = () => {
      let item = null;
      try {
        item = localStorage.getItem(key);
      } catch {}
      return item ? JSON.parse(item) as T : initialValue;
    };
    $[0] = initialValue;
    $[1] = key;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const [value, setValue] = useState(t0);
  let t1;
  let t2;
  if ($[3] !== key || $[4] !== value) {
    t1 = () => {
      const frame = window.requestAnimationFrame(() => {
        ;
        try {
          localStorage.setItem(key, JSON.stringify(value));
        } catch (t3) {
          const error = t3;
          console.error("Error saving to localStorage", error);
        }
      });
      return () => {
        window.cancelAnimationFrame(frame);
      };
    };
    t2 = [key, value];
    $[3] = key;
    $[4] = value;
    $[5] = t1;
    $[6] = t2;
  } else {
    t1 = $[5];
    t2 = $[6];
  }
  useEffect(t1, t2);
  let t3;
  if ($[7] !== value) {
    t3 = [value, setValue];
    $[7] = value;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3 as const;
}
export function ResizeContextProvider(t0) {
  const $ = _c(26);
  const {
    children,
    tableRef
  } = t0;
  const pivot = useIsPivot();
  const columns = useColumns();
  const expandable = useExpandable();
  const id = useTableId();
  const [tableWidth, setTableWidth] = useState(0);
  const [resizing, setResizing] = useState(false);
  const entities = usePivotEntities();
  const touchEnabled = useIsTouchDevice();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [requestedSize, setRequestedSize] = useLocalStorage(`requestedSize-${id}-${pivot ? "pivot" : ""}`, t1);
  let t2;
  if ($[1] !== columns || $[2] !== setRequestedSize) {
    t2 = (delta, colIndex) => {
      const columnId = columns[colIndex];
      if (!delta) {
        return;
      }
      setRequestedSize(prev => {
        const prevSize = prev[columnId];
        if (prevSize === undefined) {
          return {
            ...prev,
            [columnId]: delta
          };
        }
        return {
          ...prev,
          [columnId]: prevSize + delta
        };
      });
    };
    $[1] = columns;
    $[2] = setRequestedSize;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const onResize = t2;
  let t3;
  if ($[4] !== columns || $[5] !== entities || $[6] !== expandable || $[7] !== requestedSize || $[8] !== tableWidth) {
    t3 = calculateColumnWidths(columns, entities, requestedSize, tableWidth, expandable);
    $[4] = columns;
    $[5] = entities;
    $[6] = expandable;
    $[7] = requestedSize;
    $[8] = tableWidth;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const columnWidths = t3;
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = size => {
      if (size.width === undefined) {
        return;
      }
      setTableWidth(size.width);
    };
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const handleResize = t4;
  let t5;
  if ($[11] !== tableRef) {
    t5 = {
      ref: tableRef,
      onResize: handleResize
    };
    $[11] = tableRef;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  useResizeObserver(t5);
  let t6;
  let t7;
  if ($[13] !== columnWidths || $[14] !== resizing || $[15] !== setRequestedSize) {
    t6 = () => {
      const frame = requestAnimationFrame(() => {
        if (resizing) {
          return;
        }
        setRequestedSize(prev_0 => {
          const res = {};
          let anyChanged = false;
          for (const col of columnWidths) {
            const prevSize_0 = prev_0[col.field];
            if (prevSize_0 !== col.width) {
              anyChanged = true;
            }
            res[col.field] = col.width;
          }
          if (anyChanged) {
            return {
              ...prev_0,
              ...res
            };
          }
          return prev_0;
        });
      });
      return () => {
        window.cancelAnimationFrame(frame);
      };
    };
    t7 = [columnWidths, setRequestedSize, resizing];
    $[13] = columnWidths;
    $[14] = resizing;
    $[15] = setRequestedSize;
    $[16] = t6;
    $[17] = t7;
  } else {
    t6 = $[16];
    t7 = $[17];
  }
  useEffect(t6, t7);
  let t8;
  if ($[18] !== columnWidths || $[19] !== onResize || $[20] !== resizing || $[21] !== touchEnabled) {
    t8 = {
      columnWidths,
      onResize,
      resizing,
      setResizing,
      touchEnabled
    };
    $[18] = columnWidths;
    $[19] = onResize;
    $[20] = resizing;
    $[21] = touchEnabled;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== children || $[24] !== t8) {
    t9 = <ResizeContext.Provider value={t8}>{children}</ResizeContext.Provider>;
    $[23] = children;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
}